import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

const SESSION_KEY = "styne_intro_played";

const Preloader = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(SESSION_KEY) !== "1";
  });

  useEffect(() => {
    if (!visible) return;
    // Lock scroll while the intro plays
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, "1");
      setVisible(false);
    }, 2200);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="site-preloader"
          className="fixed inset-0 z-[9999] bg-bg flex items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: "-100%" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        >
          {/* Ambient glow */}
          <motion.div
            className="absolute inset-0 gold-glow pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.4] }}
            transition={{ duration: 1.6, times: [0, 0.5, 1] }}
          />

          {/* Grain */}
          <div
            className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Top-left corner mark */}
          <motion.div
            className="absolute top-8 left-8 text-muted text-[10px] tracking-[0.4em] uppercase"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="text-gold">●</span> Welcome
          </motion.div>
          <motion.div
            className="absolute top-8 right-8 text-muted text-[10px] tracking-[0.4em] uppercase"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Est. 2025
          </motion.div>

          {/* Progress bar bottom */}
          <motion.div
            className="absolute bottom-0 left-0 h-[2px] bg-gold origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 2.0, ease: "easeInOut" }}
            style={{ width: "100%" }}
          />

          {/* Main content */}
          <div className="relative flex flex-col items-center px-6">
            {/* Logo icon */}
            <motion.img
              src="/logo-icon.png"
              alt="Styne Group"
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
              initial={{ opacity: 0, scale: 0.7, filter: "blur(8px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />

            {/* Divider line */}
            <motion.div
              className="w-px bg-gold my-8"
              initial={{ height: 0 }}
              animate={{ height: 48 }}
              transition={{ delay: 0.9, duration: 0.5, ease: "easeOut" }}
            />

            {/* Wordmark */}
            <div className="overflow-hidden">
              <motion.div
                className="font-display font-bold text-4xl md:text-6xl tracking-widest text-ink"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 1.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                STYNE <span className="text-gold">GROUP</span>
              </motion.div>
            </div>

            {/* Sub tag */}
            <motion.div
              className="mt-5 font-body text-xs md:text-sm tracking-[0.35em] uppercase text-muted text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 0.5 }}
            >
              Plumbing <span className="text-gold">·</span> Civil <span className="text-gold">·</span> Maintenance <span className="text-gold">·</span> Infrastructure
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
